import React from 'react';
import { FilesetResolver, GestureRecognizer } from '@mediapipe/tasks-vision';
import { Action } from '../karaoke/actions';
import StartControl from './StartControl';
import PlayControl from './PlayControl';
import PauseControl from './PauseControl';
import StopControl from './StopControl';
import NextControl from './NextControl';
import PrevControl from './PrevControl';

/**
 * GestureControl
 * Responsibilities:
 *  - Initialize MediaPipe GestureRecognizer
 *  - Run per-frame recognition loop when active
 *  - Detect sustained (hold) gestures with variable thresholds
 *  - Manage MediaRecorder (start/stop/pause/resume) bound to provided videoRef's stream
 *  - Surface state patches via onStateChange and fire onGesture/onRecorded callbacks
 *  - Render gesture action buttons allowing direct trigger (no hold)
 */
export default class GestureControl extends React.Component {
  constructor(props) {
    super(props);
    this._recognizer = null;
    this._raf = 0;
    this._cancelled = false;
    this._holdAction = null;
    this._holdStartTs = 0;
    this._holdFired = false;
    this._lastStartTs = 0;
    this._mediaRecorder = null;
    this._recordedChunks = [];
    this.state = {
      activeGesture: null,
      firedGesture: null,
    };
  }

  componentDidMount() {
    this.initRecognizer();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.active !== this.props.active) {
      if (!this.props.active) this.stopLoop();
      else this.startLoop();
    }
  }

  componentWillUnmount() {
    this._cancelled = true;
    this.stopLoop();
  }

  async initRecognizer() {
    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );
      if (this._cancelled) return;
      this._recognizer = await GestureRecognizer.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            'https://storage.googleapis.com/mediapipe-models/gesture_recognizer/gesture_recognizer/float16/latest/gesture_recognizer.task',
        },
        runningMode: 'VIDEO',
        numHands: 1,
      });
      if (this._cancelled) return;
      if (this.props.active) this.startLoop();
    } catch (_) {}
  }

  getVideoEl() {
    const v = this.props.videoRef?.current?.video;
    return v && v.readyState >= 2 ? v : null;
  }

  startLoop() {
    if (this._raf) return;
    const loop = () => {
      this.recognizeFrame();
      this._raf = requestAnimationFrame(loop);
    };
    this._raf = requestAnimationFrame(loop);
  }

  stopLoop() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = 0;
  }

  recognizeFrame() {
    if (this._cancelled || !this._recognizer || !this.props.active) return;
    const video = this.getVideoEl();
    if (!video) return;
    try {
      const now = performance.now();
      const out = this._recognizer.recognizeForVideo(video, now);
      const gestures = out?.gestures || [];
      const top = gestures?.[0]?.[0];
      const landmarks = out?.landmarks?.[0];
      let action = null;
      if (top) {
        const name = top.categoryName;
  if (name === 'Thumb_Up') action = Action.START; // Thumb_Up toggles start/stop
        else if (name === 'Closed_Fist') action = Action.PAUSE;
        else if (name === 'Victory') action = Action.NEXT;
        else if (name === 'Pointing_Up') action = Action.PREV;
        else if (name === 'Open_Palm') action = Action.PLAY;
      }
      const nowMs = Date.now();
      if (action) {
        if (this.state.activeGesture !== action) this.setState({ activeGesture: action });
        if (this._holdAction !== action) {
          this._holdAction = action;
          this._holdStartTs = nowMs;
          this._holdFired = false;
        } else if (!this._holdFired) {
          let holdMs = 1500;
          if (action === Action.START) holdMs = 1000;
          else if (action === Action.PAUSE) holdMs = 500;
          else if (action === Action.PREV || action === Action.NEXT) holdMs = 300;
          if (nowMs - this._holdStartTs >= holdMs) {
            this._holdFired = true;
            const meta = top ? { name: top.categoryName, score: top.score, landmarks } : undefined;
            this.fireGesture(action, meta, true);
          }
        }
      } else {
        if (this.state.activeGesture !== null) this.setState({ activeGesture: null });
        this._holdAction = null;
        this._holdStartTs = 0;
        this._holdFired = false;
      }
    } catch (_) {}
  }

  fireGesture(action, meta, fromHold = false) {
    if (!action) return;
    this.setState({ firedGesture: action });
    if (typeof this.props.onGesture === 'function') {
      try { this.props.onGesture(action, meta); } catch (_) {}
    }
    // Debounced external start callback
    const nowMs = Date.now();
    if (action === Action.START && typeof this.props.onStartRecording === 'function') {
      if (!this._lastStartTs || nowMs - this._lastStartTs > 2000) {
        try { this.props.onStartRecording(); } catch (_) {}
        this._lastStartTs = nowMs;
      }
    }
    // Internal recording side effects only for hold-fired or button triggers
    if (action === Action.START) {
      if (!this.props.isRecording) {
        this.safeStartRecording();
      } else {
        // When stopping recording, also pause the scroller (keep position)
        this.safeStopRecording();
        // Fire PAUSE action after a brief delay so START is processed first
        setTimeout(() => {
          this.setState({ firedGesture: Action.PAUSE });
          if (typeof this.props.onGesture === 'function') {
            try { this.props.onGesture(Action.PAUSE, meta); } catch (_) {}
          }
        }, 50);
      }
    } else if (action === Action.PAUSE) this.safePauseRecording();
    else if (action === Action.PLAY) this.safeResumeRecording();
  }

  // MediaRecorder helpers
  safeStartRecording() {
    try {
      if (this.props.isRecording) return;
      const videoEl = this.getVideoEl();
      if (!videoEl) return;
      const stream = videoEl.srcObject || this.props.videoRef?.current?.stream || null;
      if (!stream) return;
      this._recordedChunks = [];
      
      // Try to use MP4 with H.264 codec if supported, otherwise fall back to WebM
      let mimeType = 'video/webm;codecs=vp9,opus';
      let fileExtension = 'webm';
      
      if (MediaRecorder.isTypeSupported('video/mp4;codecs=h264,aac')) {
        mimeType = 'video/mp4;codecs=h264,aac';
        fileExtension = 'mp4';
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        mimeType = 'video/mp4';
        fileExtension = 'mp4';
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=h264,opus')) {
        mimeType = 'video/webm;codecs=h264,opus';
        fileExtension = 'webm';
      }
      
      const mr = new MediaRecorder(stream, { mimeType });
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) this._recordedChunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(this._recordedChunks, { type: mimeType });
        blob.fileExtension = fileExtension; // Store extension for later use
        if (typeof this.props.onRecorded === 'function') {
          try { this.props.onRecorded(blob); } catch (_) {}
        }
        this.patchState({ isRecording: false, isPaused: false });
        this._mediaRecorder = null;
      };
      mr.start();
      this._mediaRecorder = mr;
      this.patchState({ isRecording: true, isPaused: false, hasRecordedVideo: false });
    } catch (_) {}
  }

  safeStopRecording() {
    try {
      if (this._mediaRecorder && (this._mediaRecorder.state === 'recording' || this._mediaRecorder.state === 'paused')) {
        if (this.props.isPaused) this.patchState({ isPaused: false });
        this._mediaRecorder.stop();
      }
    } catch (_) {}
  }

  safePauseRecording() {
    try {
      if (this._mediaRecorder && this._mediaRecorder.state === 'recording') {
        if (typeof this._mediaRecorder.pause === 'function') {
          this._mediaRecorder.pause();
          this.patchState({ isPaused: true });
        }
      }
    } catch (_) {}
  }

  safeResumeRecording() {
    try {
      if (this._mediaRecorder && this.props.isPaused) {
        if (typeof this._mediaRecorder.resume === 'function') {
          this._mediaRecorder.resume();
          this.patchState({ isPaused: false });
        }
      }
    } catch (_) {}
  }

  patchState(patch) {
    if (typeof this.props.onStateChange === 'function') {
      try { this.props.onStateChange(patch); } catch (_) {}
    }
  }

  handleIconTrigger = (action) => this.fireGesture(action, undefined, false);

  handleAction = (action) => this.fireGesture(action, undefined, false);

  render() {
    const { activeGesture } = this.state;
    const hidden = this.props.hideControls;
    if (hidden) return null;
    return (
      <div className="lrp-gestures">
        <StartControl activeAction={activeGesture} onTrigger={this.handleIconTrigger} isRecording={this.props.isRecording} />
        <PlayControl activeAction={activeGesture} onTrigger={this.handleIconTrigger} />
        <PauseControl activeAction={activeGesture} onTrigger={this.handleIconTrigger} />              
        <PrevControl activeAction={activeGesture} onTrigger={this.handleIconTrigger} />
        <NextControl activeAction={activeGesture} onTrigger={this.handleIconTrigger} />
      </div>
    );
  }
}